<script setup>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { titleStyle, titleTo } from '@/lib/titles'
import { formatDate } from '@/lib/dates'

/**
 * One title of a player, as the badge the profile shows.
 *
 * The server sends the key and the strength, already ordered: what it is called, what it looks like
 * and where it leads stay on the client (i18n and lib/titles), so the same title reads the same
 * everywhere. A champion title also carries the event it was won in, and opens it.
 */
const props = defineProps({
  /** One entry of the `titles` list built by App\Libraries\TitleLib. */
  title: { type: Object, required: true },
})

const { t, te } = useI18n()

const style = computed(() => titleStyle(props.title.key))

const to = computed(() => titleTo(props.title.key, props.title.meta ?? null))

/** The event behind a champion badge, null on the titles that do not come from one. */
const event = computed(() => props.title.meta ?? null)

/** What the badge says on hover: which event for a champion, what the title means for the rest. */
const tooltip = computed(() => {
  if (event.value) {
    return t('titles.tooltip.champion', {
      event: t(`eventTypes.${event.value.eventType}`),
      date: formatDate(event.value.dateEnd),
    })
  }

  // A title with no line of its own still explains itself: showing the key would be worse than the
  // label it already carries.
  const key = `titles.tooltip.${props.title.key}`

  return te(key) ? t(key) : t(`titles.${props.title.key}`)
})
</script>

<template>
  <v-tooltip :text="tooltip" location="top" max-width="280">
    <template #activator="{ props: activator }">
      <v-chip
        v-bind="activator"
        size="small"
        variant="tonal"
        label
        :color="style.color"
        :prepend-icon="style.icon"
        :to="to"
      >
        {{ $t(`titles.${title.key}`) }}
      </v-chip>
    </template>
  </v-tooltip>
</template>
